/**
 * Role-Based Access Control (RBAC) Configuration
 * 
 * This file defines:
 * - Default page access permissions for each role
 * - Helper functions to check permissions
 * - Page definitions with their access requirements
 */

// Define all available pages in the application
export const PAGES = {
  OVERVIEW: 'overview',
  GARAGE_VEHICLES: 'garagevehicles',
  GARAGE_REPORT: 'garage-report',
  REPORT: 'report',
  CALENDAR: 'calendar',
  INVOICES: 'invoices',
  ARCHIVE: 'archive',
  FINANCE: 'finance',
  SALES: 'sales',
  SALES_REPORT: 'sales-report',
  LINKS: 'links',
  PROMOTIONS: 'promotions',
  PARTS_LOOKUP: 'parts-lookup',
  PAYMENT: 'payment',
  TEST_ADMIN: 'test-admin',
  TECHNICIAN_DISPATCH: 'technician-dispatch', // Shown to anyone with Garage Vehicles; manager tools gated in-app
  CUSTOMERS: 'customers'
};

// Default page access permissions for each role
// true = has access, false = no access
export const DEFAULT_ROLE_PERMISSIONS = {
  owner: {
    [PAGES.OVERVIEW]: true,
    [PAGES.GARAGE_VEHICLES]: true,
    [PAGES.GARAGE_REPORT]: true,
    [PAGES.REPORT]: true,
    [PAGES.CALENDAR]: true,
    [PAGES.INVOICES]: true,
    [PAGES.ARCHIVE]: true,
    [PAGES.FINANCE]: true,
    [PAGES.SALES]: true,
    [PAGES.SALES_REPORT]: true,
    [PAGES.LINKS]: true,
    [PAGES.PROMOTIONS]: true,
    [PAGES.PARTS_LOOKUP]: true,
    [PAGES.PAYMENT]: true,
    [PAGES.TEST_ADMIN]: true,
    [PAGES.TECHNICIAN_DISPATCH]: true,
    [PAGES.CUSTOMERS]: true,
    // Owner can manage users
    canManageUsers: true,
    // Owner can access settings
    canAccessSettings: true
  },
  admin: {
    [PAGES.OVERVIEW]: true,
    [PAGES.GARAGE_VEHICLES]: true,
    [PAGES.GARAGE_REPORT]: true,
    [PAGES.REPORT]: true,
    [PAGES.CALENDAR]: true,
    [PAGES.INVOICES]: true,
    [PAGES.ARCHIVE]: true,
    [PAGES.FINANCE]: true,
    [PAGES.SALES]: true,
    [PAGES.SALES_REPORT]: true,
    [PAGES.LINKS]: true,
    [PAGES.PROMOTIONS]: true,
    [PAGES.PARTS_LOOKUP]: true,
    [PAGES.PAYMENT]: true,
    [PAGES.TEST_ADMIN]: true,
    [PAGES.TECHNICIAN_DISPATCH]: true,
    [PAGES.CUSTOMERS]: true,
    // Admin can manage users (but typically can't manage owners)
    canManageUsers: true,
    // Admin can access settings
    canAccessSettings: true
  },
  technician: {
    [PAGES.OVERVIEW]: true,
    [PAGES.GARAGE_VEHICLES]: true,
    [PAGES.GARAGE_REPORT]: true,
    [PAGES.REPORT]: true,
    [PAGES.CALENDAR]: true,
    [PAGES.INVOICES]: false,
    [PAGES.ARCHIVE]: true,
    [PAGES.FINANCE]: false,
    [PAGES.SALES]: false,
    [PAGES.SALES_REPORT]: false,
    [PAGES.LINKS]: true,
    [PAGES.PROMOTIONS]: false,
    [PAGES.PARTS_LOOKUP]: true,
    [PAGES.PAYMENT]: true, // All users should access their account
    [PAGES.TECHNICIAN_DISPATCH]: true, // Technicians see their jobs; managers get approval actions
    [PAGES.CUSTOMERS]: false,
    canManageUsers: false,
    canAccessSettings: true // All users should access settings (with limited functionality)
  },
  sales: {
    [PAGES.OVERVIEW]: true,
    [PAGES.GARAGE_VEHICLES]: true,
    [PAGES.GARAGE_REPORT]: true,
    [PAGES.REPORT]: false,
    [PAGES.CALENDAR]: true,
    [PAGES.INVOICES]: true,
    [PAGES.ARCHIVE]: true,
    [PAGES.FINANCE]: false,
    [PAGES.SALES]: true,
    [PAGES.SALES_REPORT]: true,
    [PAGES.LINKS]: true,
    [PAGES.PROMOTIONS]: true,
    [PAGES.PARTS_LOOKUP]: true,
    [PAGES.PAYMENT]: true, // All users should access their account
    [PAGES.TECHNICIAN_DISPATCH]: true, // Same workshop staff as garage board; manager actions still owner/admin-only in UI
    [PAGES.CUSTOMERS]: true,
    canManageUsers: false,
    canAccessSettings: true // All users should access settings (with limited functionality)
  }
};

/**
 * Get default permissions for a role
 * @param {string} role - User role (owner, admin, sales, technician)
 * @param {Object} customDefaults - Custom default permissions from Firestore (optional)
 * @returns {Object} Permission object
 */
export const getDefaultPermissions = (role, customDefaults = null) => {
  const hardcoded = DEFAULT_ROLE_PERMISSIONS[role] || DEFAULT_ROLE_PERMISSIONS.sales;
  // Firestore role templates are merged on top of code defaults so new pages (e.g. Technicians)
  // stay visible after deploy even if saved templates predate the new permission key.
  if (customDefaults && customDefaults[role] && typeof customDefaults[role] === 'object') {
    return { ...hardcoded, ...customDefaults[role] };
  }
  return hardcoded;
};

/**
 * Check if a user has access to a specific page
 * @param {string} role - User role
 * @param {Object} permissions - Permissions object (can be merged or custom)
 * @param {string} page - Page identifier
 * @returns {boolean} True if user has access
 */
export const hasPageAccess = (role, permissions, page) => {
  // If no role provided, deny access
  if (!role) {
    return false;
  }

  // Technicians page: anyone who can open Garage Vehicles should see it in the nav (sales, etc.).
  // Firestore may still store technician-dispatch: false on older role templates — garage access wins.
  if (page === PAGES.TECHNICIAN_DISPATCH) {
    return hasPageAccess(role, permissions, PAGES.GARAGE_VEHICLES);
  }

  // Parts Lookup lives under Garage Vehicles — same audience as the garage board.
  if (page === PAGES.PARTS_LOOKUP) {
    return hasPageAccess(role, permissions, PAGES.GARAGE_VEHICLES);
  }
  
  // If permissions are provided (merged or custom), check the page
  // Merged permissions will have all pages defined, custom permissions may only have overrides
  if (permissions && permissions[page] !== undefined) {
    // Return the permission value (true or false)
    return permissions[page] === true;
  }
  
  // If permissions object exists but page is not defined, use default
  // This handles the case where custom permissions only contain overrides
  if (permissions) {
    const defaultPerms = getDefaultPermissions(role);
    return defaultPerms[page] === true;
  }
  
  // No permissions object, use default permissions for the role
  const defaultPerms = getDefaultPermissions(role);
  return defaultPerms[page] === true;
};

/**
 * Get all pages a user can access
 * @param {string} role - User role
 * @param {Object} customPermissions - Custom permissions object (optional)
 * @returns {Array} Array of page identifiers
 */
export const getAccessiblePages = (role, customPermissions) => {
  const accessiblePages = [];
  
  // Check each page
  Object.values(PAGES).forEach(page => {
    if (hasPageAccess(role, customPermissions, page)) {
      accessiblePages.push(page);
    }
  });
  
  return accessiblePages;
};

/**
 * Check if user can manage other users
 * @param {string} role - User role
 * @param {Object} customPermissions - Custom permissions object (optional)
 * @returns {boolean} True if user can manage users
 */
export const canManageUsers = (role, customPermissions) => {
  if (customPermissions && customPermissions.canManageUsers !== undefined) {
    return customPermissions.canManageUsers === true;
  }
  
  const defaultPerms = getDefaultPermissions(role);
  return defaultPerms.canManageUsers === true;
};

/**
 * Check if user can access settings
 * @param {string} role - User role
 * @param {Object} customPermissions - Custom permissions object (optional)
 * @returns {boolean} True if user can access settings
 */
export const canAccessSettings = (role, customPermissions) => {
  // If no role provided, deny access
  if (!role) {
    return false;
  }
  
  if (customPermissions && customPermissions.canAccessSettings !== undefined) {
    return customPermissions.canAccessSettings === true;
  }
  
  const defaultPerms = getDefaultPermissions(role);
  return defaultPerms.canAccessSettings === true;
};

/**
 * Merge custom permissions with default permissions
 * @param {string} role - User role
 * @param {Object} customPermissions - Custom permissions object
 * @param {Object} customRoleDefaults - Custom default permissions from Firestore (optional)
 * @returns {Object} Merged permissions object
 */
export const mergePermissions = (role, customPermissions, customRoleDefaults = null) => {
  const defaultPerms = getDefaultPermissions(role, customRoleDefaults);
  
  if (!customPermissions || Object.keys(customPermissions).length === 0) {
    return defaultPerms;
  }
  
  // Merge custom permissions with defaults
  // Custom permissions override defaults, including false values
  const merged = {
    ...defaultPerms,
    ...customPermissions
  };
  
  // Ensure all custom permissions are explicitly set (even false values override defaults)
  Object.keys(customPermissions).forEach(page => {
    merged[page] = customPermissions[page];
  });
  
  return merged;
};

